import { Bell } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { isProfileComplete, profileNames, userAtom, userInitials } from "../../state/auth";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import UserAvatar from "./UserAvatar";

const menuTabClass = ({ isActive }: { isActive: boolean }) =>
  [
    "whitespace-nowrap px-2.5 py-2 !text-[0.85rem] !font-medium !leading-[1.5] transition-colors duration-200 min-[1280px]:px-3 min-[1280px]:!text-[0.9rem]",
    isActive ? "!text-[#3c79b4]" : "!text-[#262626] hover:!text-[#3c79b4]",
  ].join(" ");

const Navbar = () => {
  const user = useAtomValue(userAtom);
  const location = useLocation();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const profileComplete = isProfileComplete(user);
  const initials = userInitials(user);
  const { firstName, lastName } = profileNames(user);
  const displayName = profileComplete ? `${firstName} ${lastName}` : "Profile";
  const notifications = user && !profileComplete
    ? [
        {
          id: "complete-profile",
          title: "Complete your profile",
          body: "Add your first and last name so your initials appear on your account.",
          href: "/profile",
        },
      ]
    : [];

  useEffect(() => {
    setIsNotificationsOpen(false);
  }, [location.pathname]);

  return (
    <header
      className="box-border h-[4.5rem] border-b border-black/[0.07] bg-white/95"
      style={{ fontFamily: "Helvetica, sans-serif" }}
    >
      <div className="flex h-full items-center justify-between gap-3 px-4 min-[1100px]:px-10">
        <div className="flex min-w-0 items-center gap-2 min-[1100px]:gap-7">
          <Link to="/" aria-label="LIOSH home" className="inline-flex shrink-0 items-center">
            <img
              src="/LIOSH.png"
              alt="LIOSH"
              className="block h-[3.25rem] w-auto max-w-none object-contain"
            />
          </Link>

          <nav className="flex items-center" aria-label="Main">
            <NavLink to="/" end className={menuTabClass}>
              Home
            </NavLink>
            <NavLink
              to="/courses"
              className={({ isActive }) =>
                menuTabClass({
                  isActive:
                    isActive || location.pathname.startsWith("/course/")
                })
              }
            >
              My Courses
            </NavLink>
          </nav>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <div className="relative">
            <button
              type="button"
              className="relative rounded-full border border-gray-300 p-2 text-gray-700 transition-colors hover:border-[#3c79b4] hover:bg-[#f3f6f7] hover:text-[#3c79b4]"
              aria-label={
                notifications.length > 0
                  ? `Notifications, ${notifications.length} unread`
                  : "Notifications"
              }
              aria-expanded={isNotificationsOpen}
              onClick={() => setIsNotificationsOpen((prev) => !prev)}
            >
              <Bell className="h-4 w-4" />
              {notifications.length > 0 && (
                <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-[#3c79b4] ring-2 ring-white" />
              )}
            </button>

            {isNotificationsOpen && (
              <div className="absolute right-0 top-12 z-20 w-72 rounded-xl border border-[#d7e4ea] bg-white p-3 shadow-lg">
                <p className="mb-2 text-sm font-semibold text-[#262626]">Notifications</p>
                {notifications.length === 0 ? (
                  <p className="text-sm text-[#4b5563]">No notifications.</p>
                ) : (
                  <ul className="space-y-2">
                    {notifications.map((notification) => (
                      <li key={notification.id}>
                        <Link
                          to={notification.href}
                          className="block rounded-lg bg-[#f3f6f7] px-3 py-2.5 transition-colors hover:bg-[#e8f3f8]"
                          onClick={() => setIsNotificationsOpen(false)}
                        >
                          <span className="block text-sm font-medium text-[#3c79b4]">
                            {notification.title}
                          </span>
                          <span className="mt-1 block text-sm leading-5 text-[#4b5563]">
                            {notification.body}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <Link
            to="/profile"
            title={displayName}
            className="group inline-flex rounded-full transition-transform duration-200 hover:-translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3c79b4]"
            aria-label={profileComplete ? `Profile, ${displayName}` : "Profile"}
          >
            <UserAvatar initials={profileComplete ? initials : undefined} />
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
